import { Button } from "@/components/ui/baseUi/button"
import { StandardInsideInput } from "@/components/ui/forms/standardInsideInput"
import { cn } from "@/modules/lib/utils"
import {
  type ExerciseFormikType,
  type ExerciseInputKey,
} from "@/modules/sessions/sessionForm/blocks/exerciseBlock/exerciseFormikTypes"
import { HUChronoInput } from "@/modules/ui/components/huChronoInput"
import { type SimpleFormik } from "@/typings/formik"
import { PlusIcon } from "@images/svgIcons/plusIcon"
import { ChronoType } from "@modules/chrono/chronoTypes"
import { GetChronoDefaults, MAX_NUMBER_INPUT, MIN_NUMBER_INPUT } from "@modules/chrono/chronoUtils"
import { word } from "@modules/core/utils/i18n"
import { getFormErrorMessage } from "@modules/formik/formikUtils"
import { useEffect, useState } from "react"

type ChronoSettingsEMOMFormProps = {
  formik: SimpleFormik<ExerciseFormikType, ExerciseInputKey>
  className?: string
} & React.ComponentProps<"div">

export function ChronoSettingsEMOMForm({ formik, className, ...props }: ChronoSettingsEMOMFormProps) {
  const [moreThanOneSet, setMoreThanOneSet] = useState(false)

  const getFieldProps = (name: string) => formik.getFieldProps(name as ExerciseInputKey)

  useEffect(() => {
    if (!formik.values.chronoEmom) {
      void formik.setFieldValue("chronoEmom", GetChronoDefaults(ChronoType.EMOM))
    }
  }, [])

  useEffect(() => {
    if (formik.values.chronoEmom?.numberOfSets && formik.values.chronoEmom.numberOfSets > 1) {
      setMoreThanOneSet(true)
    }
  }, [formik.values.chronoEmom])

  const addSet = () => {
    if (formik.values.chronoEmom) {
      setMoreThanOneSet(true)
      void formik.setFieldValue("chronoEmom.numberOfSets", formik.values.chronoEmom.numberOfSets + 1)
    }
  }

  return (
    <div className={cn("flex flex-col gap-4", className)} {...props}>
      <div className="flex flex-col gap-1">
        <HUChronoInput
          name="chronoEmom.trainingTime"
          label={word("chrono.settings.label.training_time")}
          getFieldProps={getFieldProps}
          setFieldValue={formik.setFieldValue}
          error={getFormErrorMessage("chronoEmom.trainingTime", formik, { showAlways: true })}
        />

        <StandardInsideInput
          label={word("chrono.settings.label.rounds")}
          name="chronoEmom.numberOfRounds"
          error={getFormErrorMessage("chronoEmom.numberOfRounds", formik)}
          getFieldProps={getFieldProps}
          setFieldValue={formik.setFieldValue}
          inputProps={{
            type: "number",
            max: MAX_NUMBER_INPUT,
            min: MIN_NUMBER_INPUT,
            onChange: (e) => {
              const value = parseInt(e.target.value)
              if (value > MAX_NUMBER_INPUT) void formik.setFieldValue("chronoEmom.numberOfRounds", MAX_NUMBER_INPUT)
              else void formik.setFieldValue("chronoEmom.numberOfRounds", value)
            },
          }}
        />

        {moreThanOneSet && (
          <>
            <HUChronoInput
              name="chronoEmom.restBetweenSetsTime"
              label={word("chrono.settings.label.rest_between_sets")}
              getFieldProps={getFieldProps}
              setFieldValue={formik.setFieldValue}
              error={getFormErrorMessage("chronoEmom.restBetweenSetsTime", formik, { showAlways: true })}
            />

            <StandardInsideInput
              label={word("chrono.settings.label.number_sets")}
              name="chronoEmom.numberOfSets"
              error={getFormErrorMessage("chronoEmom.numberOfSets", formik)}
              getFieldProps={getFieldProps}
              setFieldValue={formik.setFieldValue}
              inputProps={{
                type: "number",
                max: MAX_NUMBER_INPUT,
                min: MIN_NUMBER_INPUT,
                onChange: (e) => {
                  const value = parseInt(e.target.value)
                  if (value > MAX_NUMBER_INPUT) void formik.setFieldValue("chronoEmom.numberOfSets", MAX_NUMBER_INPUT)
                  else void formik.setFieldValue("chronoEmom.numberOfSets", value)
                },
              }}
            />
          </>
        )}
      </div>

      {!moreThanOneSet && (
        <Button variant="outline" onClick={addSet}>
          <PlusIcon color="currentColor" className="text-textDefault-body" />
          {word("chrono.settings.label.add_set")}
        </Button>
      )}
    </div>
  )
}
